import { useMemo } from "react";
import type { ErrorLike } from "@apollo/client/core";
import { ProductClass } from "../types/enums";
import { toProductClass, useDataAvailability } from "./useDataAvailability";
import {
  type ClassificationConfig,
  getClassificationDisplayName,
  getQueryFieldName,
  supportsAllProductLevels,
} from "../classifications/classificationConfig";

/**
 * Hook to get configuration for all available product classifications
 * This dynamically reads from the API's dataAvailability endpoint
 */
export function useClassificationConfig(): {
  classifications: ClassificationConfig[];
  getClassificationConfig: (
    productClass: ProductClass,
  ) => ClassificationConfig | undefined;
  loading: boolean;
  error: ErrorLike | undefined;
} {
  const { dataAvailability, getYearRangeForProductClass, loading, error } =
    useDataAvailability();

  const classifications = useMemo(() => {
    return dataAvailability.map((item) => {
      const classification = toProductClass(
        item.productClassification as string,
      );
      const yearRange = getYearRangeForProductClass(classification);

      return {
        value: classification,
        label: getClassificationDisplayName(classification),
        displayName: getClassificationDisplayName(classification),
        yearRange,
        supportsAllProductLevels: supportsAllProductLevels(classification),
        queryFieldName: getQueryFieldName(classification),
      };
    });
  }, [dataAvailability, getYearRangeForProductClass]);

  const getClassificationConfig = useMemo(() => {
    return (productClass: ProductClass) => {
      return classifications.find((config) => config.value === productClass);
    };
  }, [classifications]);

  return useMemo(
    () => ({ classifications, getClassificationConfig, loading, error }),
    [classifications, getClassificationConfig, loading, error],
  );
}

/**
 * Hook to filter classifications based on current context
 * (e.g., Product Space only supports HS92, SITC doesn't support 6-digit)
 */
export function useFilteredClassifications(): ClassificationConfig[] {
  const { classifications } = useClassificationConfig();

  return useMemo(() => {
    // HS variants first (chronological), then SITC last
    const hs = classifications.filter((c) => (c.value as string) !== "SITC");
    const sitc = classifications.filter((c) => (c.value as string) === "SITC");
    return [...hs, ...sitc];
  }, [classifications]);
}
